// Typed date cells and their checks (spec §5 "Checks"). What cannot be read is the big red message;
// each rule on a read date is an error or an "Are you sure?" as `DATE_RULES` says (`date-rules.ts`).
// `readyAfterStart` is the orange cell, not a message.

import { daysBetween, isDay, shiftDayMonths } from "@axny/platform-contracts";
import { formatShortDay } from "./days";
import type { DateProblem } from "./date-rules";
import { dateProblem } from "./date-rules";

export type DateCell = "readyDate" | "schedShip" | "routedOn" | "appointmentDate" | "peShipDate";

/** READY DATE, SCHEDULED SHIP DATE and PE Order Ship Date take ASAP; Routed on and the appointment date do not. */
export const takesAsap = (cell: DateCell) => cell !== "routedOn" && cell !== "appointmentDate";

export type Parsed = { ok: true; value: string } | { ok: false; message: string };

/**
 * What a person typed, as `YYYY-MM-DD`, `ASAP` or `""` (cleared): `10/15`, `10/15/26`,
 * `10-15-2026`, `2026-10-15`, `asap`. A date without a year is this year.
 */
export function parseDateInput(text: string, cell: DateCell, today: string): Parsed {
  const t = text.trim();
  if (!t) return { ok: true, value: "" };
  if (takesAsap(cell) && /^asap$/i.test(t)) return { ok: true, value: "ASAP" };
  const bad: Parsed = {
    ok: false,
    message: `Use a date like 10/15 or 10/15/26${takesAsap(cell) ? ", or ASAP" : ""}`,
  };
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return isDay(t) ? { ok: true, value: t } : bad;
  const m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/.exec(t);
  if (!m) return bad;
  const md = `${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
  if (m[3]) {
    const year = Number(m[3]) < 100 ? Number(m[3]) + 2000 : Number(m[3]);
    const day = `${year}-${md}`;
    return isDay(day) ? { ok: true, value: day } : bad;
  }
  // No year: the one nearest today — 01/05 typed on Dec 28 is next January, 12/30 on Jan 2 last December.
  const y = Number(today.slice(0, 4));
  const near = [y - 1, y, y + 1]
    .map((year) => `${year}-${md}`)
    .filter(isDay)
    .sort((a, b) => Math.abs(daysBetween(today, a)) - Math.abs(daysBetween(today, b)))[0];
  return near ? { ok: true, value: near } : bad;
}

export interface DateContext {
  today: string;
  /** The line's PT date (latest), first ship (earliest) and cancel (earliest). */
  ptDate: string | null;
  start: string | null;
  cancel: string | null;
  /** The line's READY DATE now (a date, ASAP or ""). */
  readyDate: string;
  /** Its SCHEDULED SHIP DATE now (a date, ASAP or ""); a line on a pickup has the pickup's date. */
  schedShip?: string;
  /** The line is on a pickup: the message says so. */
  onPickup?: boolean;
}

/**
 * The checks on a parsed value: each broken rule, an error first (`DATE_RULES`). Empty when it may
 * be saved. ASAP and "" are never checked.
 */
export function checkDate(cell: DateCell, value: string, ctx: DateContext): DateProblem[] {
  if (!isDay(value)) return [];
  // More than a year away (past the same day next year, or before it last year), or before 2025 (spec §5).
  if (value < "2025-01-01" || value > shiftDayMonths(ctx.today, 12) || value < shiftDayMonths(ctx.today, -12)) {
    return [
      dateProblem("wrongYear", "That date looks wrong — check the year (it is more than a year away or before 2025)"),
    ];
  }
  const out: DateProblem[] = [];
  if (cell === "readyDate") {
    if (value < ctx.today) out.push(dateProblem("readyInPast", "READY DATE is in the past"));
    if (ctx.cancel && value > ctx.cancel)
      out.push(dateProblem("readyAfterCancel", `READY DATE is after the cancel date (${formatShortDay(ctx.cancel)})`));
    // Ship not before ready, from this side too.
    if (ctx.schedShip && isDay(ctx.schedShip) && value > ctx.schedShip)
      out.push(
        dateProblem(
          "readyAfterSchedShip",
          `READY DATE is after the SCHEDULED SHIP DATE (${formatShortDay(ctx.schedShip)})${
            ctx.onPickup ? " — the line is on a pickup that day" : ""
          }`,
        ),
      );
  }
  if (cell === "schedShip") {
    if (value < ctx.today) out.push(dateProblem("schedShipInPast", "SCHEDULED SHIP DATE is in the past"));
    if (isDay(ctx.readyDate) && value < ctx.readyDate)
      out.push(
        dateProblem(
          "schedShipBeforeReady",
          `SCHEDULED SHIP DATE is before the READY DATE (${formatShortDay(ctx.readyDate)})`,
        ),
      );
    if (ctx.cancel && value > ctx.cancel)
      out.push(
        dateProblem(
          "schedShipAfterCancel",
          `SCHEDULED SHIP DATE is after the cancel date (${formatShortDay(ctx.cancel)})`,
        ),
      );
  }
  if (cell === "routedOn") {
    if (value > ctx.today) out.push(dateProblem("routedOnInFuture", "Routed on is in the future"));
    if (ctx.ptDate && value < ctx.ptDate)
      out.push(dateProblem("routedOnBeforePtDate", `Routed on is before the PT date (${formatShortDay(ctx.ptDate)})`));
  }
  return out.sort((a, b) => Number(a.level === "warning") - Number(b.level === "warning"));
}

/** The orange cell: ready later than the first ship date (a warning, not blocked). */
export const readyAfterStart = (readyDate: string, start: string | null) =>
  isDay(readyDate) && start !== null && readyDate > start;
